import {
  Button,
  ButtonGroup,
  FieldSet,
  Form,
  Heading,
  Legend,
  Modal,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import type React from 'react';
import { useContext, useState } from 'react';
import { MessageList_GetConversationDocument } from '../../../types/GraphqlTypes';
import { preventMessageListScrollVariable } from '../../../lib/apollo';
import useModal from '../../../hooks/useModal';
import { AppContext } from '../../../contexts/AppContextProvider';
import stringUtils from '../../../utils/stringUtils';
import type { AttachmentsForMessage } from '../../MessageAttachments';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import AttachmentCardSelector from './components/AttachmentCardSelector';
import {
  useSatelliteWebConversationHandler_deleteAttachments,
  useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata,
} from '@zg-rentals/ts-satellite-client-react-query';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRefetchQueriesWithRetry } from '../../../hooks/useRefetchQueriesWithRetry';

type DeleteAttachmentsProps = {
  attachments: AttachmentsForMessage;
  messageId: string;
  conversationId: string;
  listingAlias: string;
  clientTimeZone: string;
};

export type HandleSelectAttachmentToDelete = (zDocId: string, isChecked: boolean) => void;

export default function DeleteAttachments({
  attachments,
  messageId,
  conversationId,
  listingAlias,
  clientTimeZone,
}: DeleteAttachmentsProps) {
  const { isImpersonator } = useContext(AppContext);
  const { clearModal } = useModal();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const refetchQueries = useRefetchQueriesWithRetry({
    errorContext: 'component.Modal.DeleteAttachments.handleDeleteAttachments',
  });

  const { mutate: deleteAttachments, isPending } = useSatelliteWebConversationHandler_deleteAttachments({
    meta: {
      context: {
        api: 'deleteAttachments',
        details: 'DeleteAttachmentsModal - Error deleting attachments',
      },
    },
    onSuccess: () => {
      enqueueToast(<Toast tone="success">Deleted</Toast>);
      preventMessageListScrollVariable(true);
      clearModal();

      queryClient.invalidateQueries({
        queryKey: useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata.getKey({ conversationId }),
      });

      // Refetch conversation with retry - don't block on this
      refetchQueries([
        {
          query: MessageList_GetConversationDocument,
          variables: { conversationId, listingAlias, clientTimeZone },
          name: 'MessageList',
        },
      ]);
    },
    onError: () => {
      enqueueToast(
        <Toast tone="critical">Sorry, there was an error deleting one or more attachments. Please try again.</Toast>,
      );
      clearModal();
    },
  });

  const [checkedAttachments, setCheckedAttachments] = useState<Array<string>>([]);
  const isDeleteButtonDisabled = checkedAttachments.length === 0 || isImpersonator || isPending;
  const legendText = `Select ${stringUtils.pluralize('attachment', attachments.length)} to delete`;

  const handleClose = () => clearModal();

  const handleSelectAttachmentToDelete: HandleSelectAttachmentToDelete = (zDocId, isChecked) => {
    if (isChecked) {
      setCheckedAttachments([...checkedAttachments, zDocId]);
    } else {
      const filteredAttachments = checkedAttachments.filter((checkedAttachment) => checkedAttachment !== zDocId);
      setCheckedAttachments(filteredAttachments);
    }
  };

  const handleDeleteAttachments = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const attachmentsToBeDeleted = checkedAttachments.map((zdocId) => {
      return {
        messageId,
        zdocId,
      };
    });

    deleteAttachments({
      postData: {
        conversationId,
        attachmentsToBeDeleted,
      },
    });
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      header={
        <Heading textStyle="heading-xs" level={2}>
          Delete?
        </Heading>
      }
      body={
        <Form onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
          <FieldSet legend={<Legend size="lg">{legendText}</Legend>} css={{ minWidth: '100%' }}>
            {attachments.map(({ fileName, fileType, isAttachmentExpired, isAttachmentDeleted, previewUrl, zDocId }) => {
              if (isAttachmentExpired || isAttachmentDeleted) {
                return;
              }

              const key = `${fileName}-${zDocId}`;

              return (
                <AttachmentCardSelector
                  key={key}
                  fileName={fileName || ''}
                  fileType={fileType || ''}
                  imageSrc={previewUrl || ''}
                  onSelect={handleSelectAttachmentToDelete}
                  zDocId={zDocId || ''}
                />
              );
            })}
          </FieldSet>
        </Form>
      }
      footer={
        <ButtonGroup aria-label="form actions">
          <TextButton onClick={handleClose}>Cancel</TextButton>
          <TooltipWrapper wrapInTooltip={isImpersonator}>
            <Button
              disabled={isDeleteButtonDisabled}
              emphasis="filled"
              onClick={handleDeleteAttachments}
              tone="brand"
              type="submit"
            >
              Delete
            </Button>
          </TooltipWrapper>
        </ButtonGroup>
      }
    />
  );
}
